import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";

// B2B sections (sync imports)
import HomeB2B from "./HomeB2B";
import IntroductionSection from "./IntroductionSection";
import LPnewsB2B from "./LPnewsB2B";
import RevealOnScroll from "../../../GlobalFunction/RevealOnScroll";
import Brands from "./Brands";
import CookieConsentModal from "../../../CookieConsentModal";
import Service from "./Service";

// Kept for possible future reuse.
// import AboutOverlaySection from "./AboutOverlaySection";

import AboutSection from "./AboutSection";

// ✅ NEW: scroll-following line background
import ScrollLineBackground from "./ScollLineBackground";

// language cookie helper
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import ServiceProcess from "../../ProductInfo/b2b/ServiceProcess";

/* ---------- Localized text maps ---------- */

const fonts = {
  EN: "LINE Seed Sans",
  ZH: "LINE Seed TW",
  JP: "LINE Seed JP",
};

const CTA_TEXT = {
  ZH: { label: "立即前往", aria: "前往 SEHO life 商城" },
  EN: { label: "SHOP NOW", aria: "Go to SEHO LIFE shop" },
  JP: { label: "今すぐ見る", aria: "SEHO life ショップへ移動" },
};

const ABOUT_TEXT = {
  ZH: {
    prefix: "台灣水禾的核心理念 ",
    highlight: "「潔淨初心  守護健康」",
    suffix: "",
    body: [
      "致力於為社會大眾、產業夥伴提供專業的",
      "衛生防護與清潔病媒防治方案",
      "積極推動創新、專業與永續的產品與服務",
    ],
  },

  EN: {
    prefix: "Taiwan SEHO's core belief is ",
    highlight: "“Cleanliness and Healthiness”",
    suffix: "",
    body: [
      "We are dedicated to providing the public and our industry partners",
      "reliable hygiene and pest-control solutions.",
      "Advancing Innovative, Professional, and Sustainable products.",
    ],
  },

  JP: {
    prefix: "台湾水禾は",
    highlight: "「潔浄の初心で健康を守る」",
    suffix: "",
    body: [
      "という理念を掲げ、社会や産業パートナーの皆さまに、",
      "衛生防護と害虫対策の専門的なソリューションをご提供いたします。",
      "また、革新性・専門性・",
      "持続可能性のある製品とサービスの推進に努めております。",
    ],
  },
};

/* --- Button card → B2C --- */
function ButtonCardToB2C({ lang }) {
  const navigate = useNavigate();
  const t = CTA_TEXT[lang] || CTA_TEXT.ZH;

  return (
    <Box component="section" sx={{ py: { xs: 4, md: 6 } }}>
      <Box
        sx={{ width: "100%", maxWidth: 1280, mx: "auto", px: { xs: 2, md: 3 } }}
      >
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            aspectRatio: {
              xs: "16 / 10",
              sm: "16 / 8.5",
              md: "16 / 7",
              lg: "16 / 6.3",
            },
          }}
        >
          {/* Background image (no crop) */}
          <Box
            component="img"
            src="/img/comingsoon-01.webp"
            alt="SEHO life"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              objectPosition: "center",
            }}
          />

          {/* CTA button (centered text, arrow animation) */}
          <Box
            onClick={() => navigate("/comingSoon")}
            role="button"
            aria-label={t.aria}
            sx={{
              position: "absolute",
              left: { xs: "6.5%", sm: "6.5%", md: "13.5%", lg: "13.5%" },
              top: { xs: "65%", sm: "65%", md: "60%", lg: "60%" },
              transform: "translateY(-50%)",
              width: { xs: 80, sm: 150, md: 200, lg: 220 },
              height: { xs: 20, sm: 42, md: 50, lg: 52 },
              borderRadius: 999,
              backgroundColor: "#009CA6",
              color: "#fff",
              cursor: "pointer",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: { xs: 1.5, sm: 2, md: 2.5 },
              transition:
                "transform .18s ease, box-shadow .18s ease, background-color .18s ease",
              fontWeight: 700,
              fontFamily: fonts[lang],
              fontSize: { xs: 8, sm: 13.5, md: 22 },

              "@keyframes arrowSlide": {
                "0%": { transform: "translateX(-6px)", opacity: 0.2 },
                "60%": { transform: "translateX(0)", opacity: 1 },
                "100%": { transform: "translateX(2px)", opacity: 1 },
              },
              "&:hover": {
                transform: "translateY(calc(-50% - 2px))",
              },
              "&:hover .btn-arrow": {
                animation: "arrowSlide .45s ease-out both",
              },
            }}
          >
            {/* Text perfectly centered */}
            <Box sx={{ flexGrow: 1, textAlign: "center", lineHeight: 1 }}>
              {t.label}
            </Box>

            {/* Animated arrow */}
            <ArrowForwardIcon
              className="btn-arrow"
              sx={{
                fontSize: { xs: 12, sm: 18, md: 22 },
                ml: 0.5,
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default function LandingPageB2B() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const footerRef = useRef(null);

  // ✅ hard reset scroll when entering LandingPage (prevents inherited scroll)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  // language state synced with cookie + global event
  const [lang, setLang] = useState(() => getLangFromCookie() || "ZH");

  useEffect(() => {
    const syncLang = () => setLang(getLangFromCookie() || "ZH");
    syncLang();

    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  const about = ABOUT_TEXT[lang] || ABOUT_TEXT.ZH;

  // Simple animation helper (for the other sections)
  const anim = useMemo(
    () =>
      (variantDesktop, variantMobile, duration = 1200, delay = 0) => ({
        variant: prefersReducedMotion
          ? "none"
          : isMobile
            ? variantMobile
            : variantDesktop,
        duration: prefersReducedMotion ? 0 : duration,
        delay: prefersReducedMotion ? 0 : delay,
      }),
    [isMobile, prefersReducedMotion],
  );

  const titleColor = "#808080";

  return (
    <Box
      sx={{
        width: "100%",
        overflowX: "clip",
        position: "relative",
        bgcolor: "#fffffc", // ✅ page base background (instead of per-section)
      }}
    >
      {/* ✅ line is now ABOVE page background */}
      <ScrollLineBackground prefersReducedMotion={prefersReducedMotion} />

      {/* ✅ content stays above the line */}
      <Box sx={{ position: "relative", zIndex: 2 }}>
        <CookieConsentModal mode="b2b" policyHref="/b2b/privacy" />

        <HomeB2B bgcolor="transparent" lang={lang} fonts={fonts} />

        <IntroductionSection
          lang={lang}
          fonts={fonts}
          prefersReducedMotion={prefersReducedMotion}
        />

        <ServiceProcess
          boxRevealDirection="opposite"
        />

        {/*
        <AboutOverlaySection
          lang={lang}
          fonts={fonts}
          about={about}
          prefersReducedMotion={prefersReducedMotion}
        />
        */}

        <AboutSection
          lang={lang}
          fonts={fonts}
          about={about}
          prefersReducedMotion={prefersReducedMotion}
        />

      

        <Box
          sx={{
            position: "relative",
            zIndex: 7,

            /*
             * Old AboutOverlaySection compensation.
             *
             * Keep this here for reference in case the animated
             * AboutOverlaySection is enabled again.
             *
             * mt: {
             *   xs: "-28vh",
             *   sm: "-40vh",
             *   md: "-40vh",
             * },
             */

            mt: 0,

            width: "100%",
            maxWidth: 1280,
            mx: "auto",
            px: {
              xs: 2,
              md: 3,
            },
          }}
        >
          {/* ✅ IMPORTANT: make section transparent */}
          {/* <Service lang={lang} fonts={fonts} bg="transparent" /> */}
        </Box>

        <RevealOnScroll {...anim("slide-left", "fade-up", 900)}>
          <LPnewsB2B
            titleColor={titleColor}
            bgcolor="transparent"
            lang={lang}
            fonts={fonts}
          />
        </RevealOnScroll>
        <RevealOnScroll {...anim("slide-left", "fade-up", 900)}>
          <Brands
            titleColor={titleColor}
            bgcolor="transparent"
            lang={lang}
            border={true}
            fonts={fonts}
          />
        </RevealOnScroll>

        {/* <RevealOnScroll {...anim("slide-left", "fade-up", 900)}>
          <ButtonCardToB2C lang={lang} />
        </RevealOnScroll> */}

        <Box ref={footerRef} sx={{ height: { xs: 0, sm: "10svh" } }} />
      </Box>
    </Box>
  );
}
